import { StatusIndicator } from '@neo/primitives/status-indicator'
import { humanize } from 'inflection'
import { capitalize } from 'lodash'
import { X } from 'lucide-react'
import type { FC } from 'react'
import type { NodeEvaluation } from '../../types'

interface QualityMetricProps {
  label: string
  status: string
  description: string
  isSuccess: boolean
}

const QualityMetric: FC<QualityMetricProps> = ({ label, status, description, isSuccess }) => (
  <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-4 py-2 font-inter text-xs-400 border-b border-border last:border-b-0">
    <div className="text-secondary">
      <p>{label}</p>
    </div>
    <div className="min-w-0 break-words [overflow-wrap:anywhere]">
      <StatusIndicator tone={isSuccess ? 'success' : 'danger'} className="whitespace-normal">
        {humanize(status)}
      </StatusIndicator>
      {description ? <p className="mt-1 text-secondary leading-5">{description}</p> : null}
    </div>
  </div>
)

const Section: FC<{ title: string; metrics: QualityMetricProps[] }> = ({ title, metrics }) => {
  if (!metrics.length) return null
  return (
    <div className="mb-3 last:mb-0">
      <p className="font-jetbrains text-label uppercase text-secondary mb-1">{title}</p>
      <div className="flex flex-col">
        {metrics.map((m, i) => (
          <QualityMetric key={`${m.label}-${i}`} {...m} />
        ))}
      </div>
    </div>
  )
}

interface ConversationQualityPanelProps {
  nodeEvaluation: NodeEvaluation
  chosenPath?: string
  onClose?: () => void
}

/**
 * Richer AO Conversation Quality panel — the flag-on ("AO") view rendered
 * inside a clickable, wider, scrollable popover on the run-detail node header.
 * Shows node-scoped judges only; call-level detections (voicemail, low
 * engagement, user_never_spoke, …) and goals live in the separate Call Quality
 * / Conversation Goals accordions, so they are deliberately NOT repeated here.
 * The flag-off legacy view keeps the compact hover tooltip unchanged.
 */
export const ConversationQualityPanel: FC<ConversationQualityPanelProps> = ({
  nodeEvaluation,
  chosenPath,
  onClose,
}) => {
  const {
    hallucination,
    instructions_adherence,
    intent_identification,
    node_loop,
    variable_extraction,
  } = nodeEvaluation

  const nodeMetrics: QualityMetricProps[] = [
    {
      label: 'Instruction Following',
      status: instructions_adherence.adherence_passed ? 'Passed' : 'Failed',
      description: instructions_adherence.reason || 'All required instructions satisfied',
      isSuccess: instructions_adherence.adherence_passed,
    },
    {
      label: 'Loop Detection',
      status: node_loop.loop_detected ? 'Loop detected' : 'No loop detected',
      description: node_loop.reason || 'Conversation progressed linearly',
      isSuccess: !node_loop.loop_detected,
    },
    {
      label: 'Hallucination Analysis',
      status: hallucination.hallucinated ? 'Detected' : 'Not detected',
      description: hallucination.reason || 'Agent responses matched grounding context',
      isSuccess: !hallucination.hallucinated,
    },
    {
      label: 'Intent Detection',
      status: chosenPath
        ? capitalize(chosenPath)
        : intent_identification.intent_wrongly_identified
          ? 'Wrongly identified'
          : intent_identification.intent_not_found
            ? 'Not detected'
            : 'Identified',
      description: intent_identification.reason || 'Detected from dialogue cues',
      isSuccess:
        !intent_identification.intent_not_found && !intent_identification.intent_wrongly_identified,
    },
  ]

  if (variable_extraction) {
    const missing = variable_extraction.missing_variables?.length
      ? ` Missing: ${variable_extraction.missing_variables.join(', ')}.`
      : ''
    nodeMetrics.push({
      label: 'Variable Extraction',
      status: variable_extraction.extraction_successful ? 'Successful' : 'Failed',
      description: (variable_extraction.reason || 'Required variables captured') + missing,
      isSuccess: variable_extraction.extraction_successful,
    })
  }

  return (
    <div
      className="flex flex-col max-h-[min(70vh,var(--radix-popover-content-available-height,70vh))] bg-popover text-popover-foreground"
      data-testid="conversation-quality"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <h3 className="text-h4-500">Conversation Quality</h3>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="button-close-conversation-quality"
            className="rounded text-secondary hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>
      <div className="overflow-y-auto subtle-scrollbar px-4 py-3">
        <Section title="Node quality" metrics={nodeMetrics} />
      </div>
    </div>
  )
}
